import {Context, FC, PropsWithChildren, useCallback, useEffect, useMemo, useState} from "react";
import {
    confirmSignUp,
    fetchAuthSession,
    signIn,
    signInWithRedirect,
    confirmSignIn,
    signOut,
    signUp,
    resetPassword,
    confirmResetPassword,
    updatePassword,
    updateUserAttributes,
    JWT,
} from "@aws-amplify/auth";
import {AuthContextType, AuthState, SignUpInput} from "../context/Auth";
import LoadingScreen from "@memaro/components/LoadingScreen.tsx";
import {StepConfirmSignInWithNewPasswordRequired} from "../constants/auth.ts";

type AuthProviderProps = PropsWithChildren

export type JWTPayload = JWT["payload"]

export type UpdatedUserAttributes = Parameters<typeof updateUserAttributes>[0]["userAttributes"]

export type UserAttributeMappers<UserAttributes> = {
    userAttributeMapper: (attributes: JWTPayload) => UserAttributes
    reverseUserAttributeMapper: (attributes: Partial<UserAttributes>) => UpdatedUserAttributes
}

type CreateAuthProviderInput<UserAttributes> = UserAttributeMappers<UserAttributes> & {
    authContext: Context<AuthContextType<UserAttributes>>
    userAttributeMapper: (attributes: JWTPayload) => UserAttributes
    reverseUserAttributeMapper: (attributes: UserAttributes) => UpdatedUserAttributes
}

export type AuthProvider = FC<AuthProviderProps>

export const createAuthProvider = <UserAttributes,>(input: CreateAuthProviderInput<UserAttributes>): AuthProvider => {
    return ({children}: AuthProviderProps) => {
        const [signUpUsername, setSignUpUsername] = useState<string | undefined>()
        const [accessToken, setAccessToken] = useState<string | undefined>()
        const [username, setUsername] = useState<string | undefined>()
        const [userAttributes, setUserAttributes] = useState<UserAttributes | undefined>()
        const [state, setState] = useState<AuthState>('initialising')

        const handleSignedIn = useCallback(async (forceRefresh: boolean = false) => {
            let authenticated = false
            try {
                const authSession = await fetchAuthSession({forceRefresh: forceRefresh})
                setAccessToken(authSession.tokens?.accessToken.toString())
                setUsername(authSession.tokens?.idToken?.payload["email"] as string | undefined)
                setUserAttributes(input.userAttributeMapper(authSession.tokens?.idToken?.payload ?? {}))
                authenticated = Boolean(authSession.tokens?.accessToken)
            } catch {
                // do nothing
            } finally {
                setState(authenticated ? 'authenticated' : 'unauthenticated')
            }
        }, [])

        useEffect(() => {
            handleSignedIn();
        }, [handleSignedIn])

        const handleSignIn: typeof signIn = useCallback(async (input) => {
            const output = await signIn(input)
            if (output.nextStep.signInStep == StepConfirmSignInWithNewPasswordRequired) {
                setState("confirm_sign_in_with_new_password")
            } else {
                await handleSignedIn()
            }
            return output
        }, [handleSignedIn])

        const handleConfirmSignIn: typeof confirmSignIn = useCallback(async (input) => {
            const output = await confirmSignIn(input)
            await handleSignedIn()
            return output
        }, [])

        const handleSignUp = useCallback((signupInput: SignUpInput<UserAttributes>) => {
            setSignUpUsername(signupInput.email)
            return signUp({
                username: signupInput.email,
                password: signupInput.password,
                options: {
                    userAttributes: {
                        email: signupInput.email,
                        ...input.reverseUserAttributeMapper(signupInput.attributes),
                    }
                }
            })
        }, [])

        const handleSignOut: typeof signOut = useCallback(async (input) => {
            return signOut(input).finally(() => {
                setAccessToken(undefined)
                setState("unauthenticated")
            })
        }, [])

        const handleResetPassword: typeof resetPassword = useCallback((input) => {
            setSignUpUsername(input.username)
            return resetPassword(input)
        }, [])

        const handleUpdateUserAttributes = useCallback(async (updateInput: Partial<UserAttributes>) => {
            await updateUserAttributes({
                userAttributes: input.reverseUserAttributeMapper(updateInput),
            });
            await handleSignedIn(true)
        }, [handleSignedIn])

        const value = useMemo((): AuthContextType<UserAttributes> => ({
            state,
            accessToken,
            username,
            userAttributes,
            signIn: handleSignIn,
            signInWithRedirect,
            confirmSignIn: handleConfirmSignIn,
            signUp: handleSignUp,
            confirmSignUp,
            signOut: handleSignOut,
            signUpUsername,
            resetPassword: handleResetPassword,
            confirmResetPassword,
            updatePassword,
            updateUserAttributes: handleUpdateUserAttributes,
        }), [state, accessToken, username, userAttributes, handleSignIn, handleSignUp, handleSignOut, signUpUsername, handleResetPassword, handleUpdateUserAttributes])

        return state == 'initialising' ? <LoadingScreen/> : <input.authContext.Provider value={value}>{children}</input.authContext.Provider>
    }
}
